import React from 'react';
import { Button } from '../ui/button';
import { Icon } from './Icon';
import { withTranslation } from 'react-i18next';

/**
 * RouteErrorBoundary — catches render-time crashes in a route subtree
 * so the page never goes blank.
 *
 * Why we need this: a production-only TDZ ("Cannot access 'X' before
 * initialization") inside any descendant component will unmount the
 * entire route and leave a white page. This boundary keeps the header
 * intact and surfaces a friendly error UI so the user can recover or
 * go home, rather than facing a blank screen.
 *
 * Props:
 *   children     — subtree to guard
 *   label        — short description shown in the heading (e.g. "Repair flow")
 *   homePath     — Recover-by-going-home destination (default "/")
 *   onResetExtra — optional callback invoked alongside the boundary reset
 */
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.label || 'Route'}] render crash:`, error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onResetExtra === 'function') {
      try { this.props.onResetExtra(); } catch { /* non-fatal */ }
    }
  };

  handleReload = () => {
    try { window.location.reload(); } catch { /* SSR / non-browser */ }
  };

  handleGoHome = () => {
    try { window.location.assign(this.props.homePath || '/'); } catch { /* non-browser */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { t } = this.props;
    const message = String(
      this.state.error?.message
        || this.state.error
        || t('errors.unknownRenderError')
    );

    return (
      <div className="max-w-xl mx-auto py-12 px-4" data-testid="route-error-boundary">
        <div className="rounded-xl border border-red-300 bg-red-50 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <Icon name="alert-triangle" className="h-5 w-5 text-red-600" />
            </div>
            <div className="space-y-1">
              <h2 className="font-heading text-lg font-bold text-red-900">
                {t('errors.pageLoadTitle')}
              </h2>
              <p className="text-sm text-red-800/90">
                {this.props.label
                  ? t('errors.pageLoadWithLabel', { label: this.props.label })
                  : t('errors.pageLoadGeneric')}
              </p>
            </div>
          </div>

          <details className="text-xs">
            <summary className="cursor-pointer text-red-700/80 select-none">
              {t('errors.technicalDetails')}
            </summary>
            <pre className="mt-2 p-2 bg-white/60 border border-red-200 rounded font-mono text-[11px] text-red-800 break-all whitespace-pre-wrap">
              {message}
            </pre>
          </details>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={this.handleReset}
              data-testid="route-error-retry"
            >
              <Icon name="refresh-cw" className="h-4 w-4 me-1.5" /> {t('errors.tryAgain')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={this.handleReload}
              data-testid="route-error-reload"
            >
              {t('errors.reloadPage')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={this.handleGoHome}
              data-testid="route-error-home"
            >
              <Icon name="home" className="h-4 w-4 me-1.5" /> {t('errors.goHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}


export default withTranslation('public')(RouteErrorBoundary);
